import type {
  CpiArtefact,
  ExpensesArtefact,
  PopulationArtefact,
} from "./types";

// Pure citation/meta helpers (no node:fs) so the client MultiViewTrendChart
// can import from this module without pulling server-only code into the
// client bundle. The fs-backed loaders live in ./data.

/** The single primary source URL (City of Calgary 2024 AFR). */
export function afrUrl(artefact: ExpensesArtefact): string {
  return artefact.meta.sources[0]?.url ?? "";
}

/** Page-anchored deep link: `${url}#page=N` (matches the /finances
 *  page-anchor convention). */
export function sourcePageHref(url: string, page: number | null): string {
  return page ? `${url}#page=${page}` : url;
}

/**
 * Combined provenance for the three-view trend (COMPONENTS.md "Combined
 * provenance" — a figure drawing on multiple artefacts gets a merged meta
 * whose sources concatenate and whose derivation describes the
 * combination). Combines the expenses artefact (nominal), the shared
 * Calgary CPI (inflation-adjusted), and the shared population estimate
 * (per-capita).
 */
export function buildTrendMeta(
  expenses: ExpensesArtefact,
  cpi: CpiArtefact,
  population: PopulationArtefact,
): ExpensesArtefact["meta"] {
  return {
    ...expenses.meta,
    sources: [
      ...expenses.meta.sources,
      ...cpi.meta.sources,
      ...population.meta.sources,
    ],
    derivation:
      `${expenses.meta.derivation}\n\nThe three-view trend combines this artefact (nominal total expenses) with the shared Calgary all-items CPI artefact (inflation-adjusted view, base year ` +
      `${expenses.derived.inflation_base_year}) and the shared Calgary population artefact (per-capita view). Per-view and per-point source citations are rendered inline; this footer carries the full concatenated provenance.`,
  };
}

export type TrendPoint = {
  year: number;
  /** raw value in the view's native unit (thousands of CAD, or dollars
   *  for per-capita). null when the view cannot be computed for the year. */
  value: number | null;
  /** per-point primary citation (FR-006 — source named inline). */
  citation: { label: string; href: string } | null;
};

export type TrendViewId = "nominal" | "inflation-adjusted" | "per-capita";

export type TrendView = {
  id: TrendViewId;
  label: string;
  /** axis/format hint: "thousands" → $M, "dollars" → $ per person. */
  unitKind: "thousands" | "dollars";
  unitLabel: string;
  points: TrendPoint[];
  /** view-level source line rendered beneath the chart (FR-006). */
  sourceLine: { label: string; href: string };
  footnote: string;
};

/**
 * The view-switch reducer the MultiViewTrendChart toggle calls. Extracted
 * as a pure function so the switching behaviour (active view, its
 * source line, unit, and plotted points) is unit-testable without a DOM —
 * the component is a thin wrapper that renders `selectView(views, id)`.
 */
export function selectView(views: TrendView[], id: TrendViewId): TrendView {
  return views.find((v) => v.id === id) ?? views[0];
}

export type ServiceAreaRow = {
  canonical_id: string;
  label: string;
  description: string | null;
  crossLink: string | null;
  valueThousands: number;
  share: number;
  sourcePage: number | null;
};

/**
 * Service-area rows for the most recent reporting year (US1). The five
 * audited segments ARE the total, so shares sum to 1; sorted largest
 * first (the canonicalizer already orders them this way, but we sort
 * defensively so the table is stable if the artefact order changes).
 */
export function serviceAreaRows(artefact: ExpensesArtefact): ServiceAreaRow[] {
  const year = artefact.derived.most_recent_year;
  const shares = artefact.derived.shares_by_year[String(year)] ?? {};
  const rows: ServiceAreaRow[] = [];
  for (const sa of artefact.service_areas) {
    const rec = sa.year_records.find((r) => r.year === year);
    if (!rec || rec.value_thousands === null) continue;
    rows.push({
      canonical_id: sa.canonical_id,
      label: sa.current_label,
      description: sa.description,
      crossLink: sa.cross_link,
      valueThousands: rec.value_thousands,
      share: shares[sa.canonical_id] ?? 0,
      sourcePage: rec.source_page,
    });
  }
  return rows.sort((a, b) => b.valueThousands - a.valueThousands);
}

function pointsFrom(
  years: number[],
  byYear: Record<string, number>,
  citationFor: (year: number) => { label: string; href: string } | null,
): TrendPoint[] {
  return years.map((y) => {
    const v = byYear[String(y)];
    return {
      year: y,
      value: v === undefined ? null : v,
      citation: v === undefined ? null : citationFor(y),
    };
  });
}

/**
 * The three switchable trend views (FR-005). Every figure is read from
 * `artefact.derived` (computed once in the pipeline,
 * feedback_math_in_code); this module only reshapes + attaches the
 * inline source citations the spec requires (FR-006):
 *  - nominal & inflation-adjusted cite the City AFR (page-anchored);
 *  - inflation-adjusted additionally names the StatCan CPI series + base
 *    year in its source line;
 *  - per-capita names the City population estimate source per year.
 */
export function buildTrendViews(
  artefact: ExpensesArtefact,
  population: PopulationArtefact,
  cpi: CpiArtefact,
): TrendView[] {
  const years = artefact.reporting_years;
  const d = artefact.derived;
  const url = afrUrl(artefact);

  // total_expenses figures are all page-anchored to the same AFR page
  // (the Five-Year "Expenses by Object Unaudited" review).
  const totalPageByYear: Record<number, number | null> = {};
  for (const [y, fig] of Object.entries(artefact.total_expenses)) {
    totalPageByYear[Number(y)] = fig.source_page;
  }
  const afrCite = (y: number) => ({
    label: `City of Calgary 2024 AFR · p.${totalPageByYear[y] ?? ""}`,
    href: sourcePageHref(url, totalPageByYear[y] ?? null),
  });

  const cpiUrl = cpi.meta.sources[0]?.url ?? url;
  const baseYear = d.inflation_base_year;

  const popByYear = new Map(population.estimates.map((e) => [e.year, e]));
  const popCite = (y: number) => {
    const e = popByYear.get(y);
    if (!e) return null;
    const page = e.source.page ?? null;
    return {
      label: `City of Calgary population ${y} (${e.status})${
        page ? ` · p.${page}` : ""
      }`,
      href: page ? `${e.source.url}#page=${page}` : e.source.url,
    };
  };

  return [
    {
      id: "nominal",
      label: "Nominal",
      unitKind: "thousands",
      unitLabel: "$ millions (as reported)",
      points: pointsFrom(years, d.trend_nominal_thousands, afrCite),
      sourceLine: {
        label:
          "City of Calgary 2024 Annual Financial Report — Five-Year Statistical Review, Expenses by Object total row (the only table carrying the 2020 comparative), p.118",
        href: sourcePageHref(url, totalPageByYear[artefact.derived.most_recent_year] ?? null),
      },
      footnote:
        "Total consolidated expenses as reported — not adjusted for inflation or population. Useful for the raw dollar figure, misleading for “spending grew X%” claims.",
    },
    {
      id: "inflation-adjusted",
      label: `Inflation-adjusted (${baseYear} $)`,
      unitKind: "thousands",
      unitLabel: `$ millions (constant ${baseYear} dollars)`,
      points: pointsFrom(years, d.trend_inflation_adjusted_thousands, afrCite),
      sourceLine: {
        label: `Deflated by ${d.cpi_series_used}, base year ${baseYear}`,
        href: cpiUrl,
      },
      footnote: `Each year’s total restated into constant ${baseYear} dollars using the Calgary all-items CPI. ${baseYear} is the base year, so its inflation-adjusted value equals its nominal value. This is the right view for “did real spending grow?”`,
    },
    {
      id: "per-capita",
      label: "Per capita",
      unitKind: "dollars",
      unitLabel: "$ per Calgarian",
      points: pointsFrom(years, d.trend_per_capita_dollars, popCite),
      sourceLine: {
        // Each year's denominator has its own City source (see the
        // per-point citations); the view-level link points at the shared
        // population artefact that carries all years, not any single
        // year's AFR.
        label:
          "City of Calgary annual population estimates — shared denominator (same source as /finances/revenues; provincial CMA estimates excluded)",
        href: "/data/shared/population-calgary.json",
      },
      footnote:
        "Total expenses divided by the City of Calgary population estimate for each year. The right view for “is the City spending more per resident?”",
    },
  ];
}
